import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';

/**
 * Dial policies: the data behind the account's Dialling restrictions screen and
 * an extension's Dialling restrictions tab.
 */

export type Continent =
  | 'africa' | 'asia' | 'europe' | 'north_america' | 'south_america' | 'oceania' | 'networks';

export interface Country {
  iso2: string; name: string; dial_prefix: string; is_high_risk: boolean; continent: Continent;
}

/** In the order shown; the account's own continent is moved to the top. */
export const CONTINENTS: ReadonlyArray<readonly [Continent, string]> = [
  ['africa', 'Africa'], ['europe', 'Europe'], ['asia', 'Asia'],
  ['north_america', 'North America'], ['south_america', 'South America'],
  ['oceania', 'Oceania'], ['networks', 'Satellite and international networks'],
];

/** The three methods, as a policy holds them. */
export interface PolicyRules {
  /** International dialling. Off means national numbers only. */
  allow_international: boolean;
  allowed_countries: string[];
  /** High-risk destinations inside a country that is switched on. */
  allow_premium: boolean;
  blocked_prefixes: string[];
  allowed_prefixes: string[];
}

export interface Policy extends PolicyRules {
  id: string; name: string;
  extension_count: number; group_count: number; tenant_count: number;
}

export interface Assignments {
  home_country: string;
  /** The account country's dialling code, for prefixes written in national form. */
  home_prefix: string | null;
  account_policy_id: string | null;
  groups: Array<{ id: string; name: string; dial_policy_id: string | null; extension_count: number }>;
  seats_with_own_policy: number;
}

export const dpKeys = {
  countries: ['countries'] as const,
  policies: (t: string) => ['dial-policies', t] as const,
  assignments: (t: string) => ['dial-policy-assignments', t] as const,
  effective: (t: string, extensionId: string) => ['extension-dial-policy', t, extensionId] as const,
};

export const useCountries = () =>
  useQuery({
    queryKey: dpKeys.countries,
    queryFn: () => api.get<{ data: Country[] }>('/v1/countries'),
    // Reference data: it changes when the ITU does, not when anybody here acts.
    staleTime: 24 * 60 * 60 * 1000,
  });

export const usePolicies = (tenantId: string) =>
  useQuery({
    queryKey: dpKeys.policies(tenantId),
    queryFn: () => api.get<{ data: Policy[] }>(`/v1/tenants/${tenantId}/dial-policies`),
    enabled: !!tenantId,
  });

export const useAssignments = (tenantId: string) =>
  useQuery({
    queryKey: dpKeys.assignments(tenantId),
    queryFn: () => api.get<Assignments>(`/v1/tenants/${tenantId}/dial-policy-assignments`),
    enabled: !!tenantId,
  });

/** Everything that shows a policy's effect, since any change to one moves them all. */
function useRefreshAll(tenantId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: dpKeys.policies(tenantId) });
    void qc.invalidateQueries({ queryKey: dpKeys.assignments(tenantId) });
    void qc.invalidateQueries({ queryKey: ['extension-dial-policy', tenantId] });
  };
}

export function useSavePolicy(tenantId: string) {
  const refresh = useRefreshAll(tenantId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; name: string } & PolicyRules) =>
      id ? api.patch(`/v1/tenants/${tenantId}/dial-policies/${id}`, body)
         : api.post(`/v1/tenants/${tenantId}/dial-policies`, body),
    onSuccess: refresh,
  });
}

export function useDeletePolicy(tenantId: string) {
  const refresh = useRefreshAll(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean; fell_back: number }>(`/v1/tenants/${tenantId}/dial-policies/${id}`),
    onSuccess: refresh,
  });
}

/** Attaches a policy to the account (groupId null) or a cost centre; policyId null detaches it. */
export function useAssignPolicy(tenantId: string) {
  const refresh = useRefreshAll(tenantId);
  return useMutation({
    mutationFn: ({ groupId, policyId }: { groupId: string | null; policyId: string | null }) =>
      api.put(groupId
        ? `/v1/tenants/${tenantId}/admin-groups/${groupId}/dial-policy`
        : `/v1/tenants/${tenantId}/dial-policy`, { dial_policy_id: policyId }),
    onSuccess: refresh,
  });
}

/** The old list syntax marked a prefix with a trailing %; every entry is one anyway. */
export const barePrefix = (p: string) => p.replace(/%$/, '');

/**
 * A prefix as people write one, turned into the form the switch compares.
 *
 * The switch matches international digits without the plus (27861), because
 * that is what a dialled number becomes once normalised. People write 0861 for
 * a number at home and +44 70 or 0044 70 for one abroad; each is converted, so
 * the list holds one form and a duplicate shows up as one.
 */
export function normalisePrefix(raw: string, homePrefix: string | null):
  { value: string } | { error: string } {
  let s = barePrefix(raw.replace(/[\s().-]/g, ''));
  if (s.startsWith('+')) s = s.slice(1);
  else if (s.startsWith('00')) s = s.slice(2);
  else if (s.startsWith('0')) {
    if (!homePrefix) return { error: 'write it with the country code' };
    s = homePrefix + s.slice(1);
  }
  if (!/^\d+$/.test(s)) return { error: 'digits only' };
  if (s.length > 15) return { error: 'longer than any number' };
  return { value: s };
}

/** Shown the way it would be dialled: 0861 at home, +882 abroad. */
export function showPrefix(value: string, homePrefix: string | null): string {
  const v = barePrefix(value);
  return homePrefix && v.startsWith(homePrefix) && v.length > homePrefix.length
    ? '0' + v.slice(homePrefix.length)
    : '+' + v;
}

/** A country code as written: +44, and +1 876 inside the North American plan. */
export function dialCode(prefix: string): string {
  return prefix.length === 4 && prefix.startsWith('1') ? `+1 ${prefix.slice(1)}` : `+${prefix}`;
}
